import { getFirestore, doc, setDoc, getDoc, updateDoc, onSnapshot, collection, query, where, getDocs } from 'firebase/firestore';

const db = getFirestore();

// Interfaces
export interface EmployeeAssignment {
  employeeId: string;
  businessId: string;
  department: string;
  role: string;
  position: string;
  hireDate: string;
  salary?: number;
  status: 'active' | 'inactive' | 'pending';
  assignedAt: number;
  assignedBy: string;
  employeeCode: string;
}

export interface LinkageCode {
  code: string;
  userId: string;
  timestamp: number;
  expiresAt: number;
  isActive: boolean;
}

// Servicio principal
class LinkageService {
  async generateEmployeeCode(userId: string): Promise<LinkageCode> {
    const code = this.generateUniqueCode(userId);
    const now = Date.now();
    const expiresAt = now + (30 * 1000);

    const linkageCode: LinkageCode = {
      code,
      userId,
      timestamp: now,
      expiresAt,
      isActive: true
    };

    await setDoc(doc(db, 'linkageCodes', userId), linkageCode);
    return linkageCode;
  }

  private generateUniqueCode(userId: string): string {
    const timestamp = Math.floor(Date.now() / 30000);
    const hash = userId + timestamp.toString();
    
    let code = '';
    for (let i = 0; i < 8; i++) {
      const charCode = hash.charCodeAt(i % hash.length);
      code += String.fromCharCode(65 + (charCode % 26));
    }
    
    return code;
  }

  subscribeToEmployeeCode(userId: string, callback: (code: LinkageCode | null) => void) {
    return onSnapshot(doc(db, 'linkageCodes', userId), (doc) => {
      if (doc.exists()) {
        const data = doc.data() as LinkageCode;
        callback(data);
      } else {
        callback(null);
      }
    });
  }

  async findEmployeeByCode(scannedCode: string): Promise<{ userId: string; userData: any } | null> {
    try {
      const linkageQuery = query(
        collection(db, 'linkageCodes'),
        where('code', '==', scannedCode),
        where('isActive', '==', true)
      );
      
      const linkageDocs = await getDocs(linkageQuery);
      
      if (linkageDocs.empty) {
        return null;
      }

      const linkageDoc = linkageDocs.docs[0];
      const linkageData = linkageDoc.data() as LinkageCode;
      
      if (linkageData.expiresAt < Date.now()) {
        return null;
      }

      const userDoc = await getDoc(doc(db, 'users', linkageData.userId));
      if (!userDoc.exists()) {
        return null;
      }

      return {
        userId: linkageData.userId,
        userData: userDoc.data()
      };
    } catch (error) {
      console.error('Error finding employee by code:', error);
      return null;
    }
  }

  async assignEmployeeToBusiness(
    businessId: string,
    employeeId: string,
    employeeCode: string,
    assignmentData: {
      department: string;
      role: string;
      position: string;
      hireDate: string;
      salary?: number;
      assignedBy: string;
    }
  ): Promise<boolean> {
    try {
      const employeeAssignment: EmployeeAssignment = {
        employeeId,
        businessId,
        department: assignmentData.department,
        role: assignmentData.role,
        position: assignmentData.position,
        hireDate: assignmentData.hireDate,
        salary: assignmentData.salary,
        status: 'active',
        assignedAt: Date.now(),
        assignedBy: assignmentData.assignedBy,
        employeeCode
      };

      await setDoc(
        doc(db, 'businesses', businessId, 'employees', employeeId),
        employeeAssignment
      );

      await updateDoc(doc(db, 'users', employeeId), {
        business: businessId,
        department: assignmentData.department,
        role: assignmentData.role,
        position: assignmentData.position,
        hireDate: assignmentData.hireDate,
        updatedAt: Date.now()
      });

      await updateDoc(doc(db, 'linkageCodes', employeeId), {
        isActive: false
      });

      return true;
    } catch (error) {
      console.error('Error assigning employee to business:', error);
      return false;
    }
  }

  async getBusinessEmployees(businessId: string): Promise<EmployeeAssignment[]> {
    try {
      const employeesQuery = query(
        collection(db, 'businesses', businessId, 'employees'),
        where('status', '==', 'active')
      );
      
      const employeesDocs = await getDocs(employeesQuery);
      return employeesDocs.docs.map(doc => doc.data() as EmployeeAssignment);
    } catch (error) {
      console.error('Error getting business employees:', error);
      return [];
    }
  }

  subscribeToBusinessEmployees(businessId: string, callback: (employees: EmployeeAssignment[]) => void) {
    const employeesQuery = query(
      collection(db, 'businesses', businessId, 'employees'),
      where('status', '==', 'active')
    );
    
    return onSnapshot(employeesQuery, (snapshot) => {
      const employees = snapshot.docs.map(doc => doc.data() as EmployeeAssignment);
      callback(employees);
    });
  }

  async isUserAssignedToBusiness(userId: string, businessId: string): Promise<boolean> {
    try {
      const employeeDoc = await getDoc(doc(db, 'businesses', businessId, 'employees', userId));
      return employeeDoc.exists();
    } catch (error) {
      console.error('Error checking user assignment:', error);
      return false;
    }
  }

  async removeEmployeeFromBusiness(businessId: string, employeeId: string): Promise<boolean> {
    try {
      await updateDoc(doc(db, 'businesses', businessId, 'employees', employeeId), {
        status: 'inactive',
        removedAt: Date.now()
      });

      await updateDoc(doc(db, 'users', employeeId), {
        business: 'none',
        department: null,
        role: null,
        position: null,
        updatedAt: Date.now()
      });

      return true;
    } catch (error) {
      console.error('Error removing employee from business:', error);
      return false;
    }
  }
}

// Exportar la instancia del servicio
export const linkageService = new LinkageService(); 